<template>
  <b-modal
    v-model="localValue"
    title="Cách tính chỉ số (báo cáo theo IMEI)"
    size="xl"
    hide-footer
    scrollable
  >
    <p class="text-muted small mb-3">
      Mỗi dòng báo cáo tương ứng <strong>một mã IMEI</strong> trên một hoá đơn. Các khoản cấp hoá đơn
      (chiết khấu bill, phí giao hàng, thanh toán điểm) được
      <strong>phân bổ xuống từng IMEI</strong> theo tỷ lệ giá trị dòng sau chiết khấu item
    </p>
    <b-table
      :items="formulaItems"
      :fields="formulaFields"
      bordered
      small
      hover
      class="formula-table"
    >
      <template v-slot:cell(stt)="row">
        <div class="text-center font-weight-bold">{{ row.item.stt }}</div>
      </template>
      <template v-slot:cell(formula)="row">
        <code class="formula-code">{{ row.item.formula }}</code>
      </template>
    </b-table>
  </b-modal>
</template>

<script>
export default {
  name: 'ProductImeiFormulaExplanationModal',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    localValue: {
      get() {
        return this.value
      },
      set(next) {
        this.$emit('input', next)
      },
    },
  },
  data() {
    return {
      formulaFields: [
        { key: 'stt', label: 'STT', thStyle: { width: '60px', textAlign: 'center' } },
        { key: 'term', label: 'Thuật ngữ', thStyle: { width: '200px', fontWeight: 600 } },
        { key: 'meaning', label: 'Ý nghĩa', thStyle: { fontWeight: 600 } },
        { key: 'formula', label: 'Công thức / ghi chú', thStyle: { width: '340px', fontWeight: 600 } },
      ],
      formulaItems: [
        {
          stt: '—',
          term: 'Phân bổ bill → IMEI',
          meaning:
            'Chiết khấu cấp bill, phí GH khách/shop, thanh toán điểm được chia cho từng dòng IMEI theo tỷ lệ (itemAmount / tổng itemAmount của các dòng IMEI trong bill).',
          formula:
            'itemAmount = max(0, giá × SL − CK item). Phân bổ: floor(share × tổng), phần dư → dòng itemAmount lớn nhất.',
        },
        {
          stt: '(1.1)',
          term: 'Giá bán (trước VAT)',
          meaning:
            'Giá trị trước thuế của dòng IMEI (đã bóc VAT từ giá bán theo VAT sản phẩm), trước chiết khấu. HĐ trả hàng nhân hệ số −1. Đây chính là "Tiền hàng thực bán".',
          formula: 'Giá bán (trước VAT) = (Giá trước thuế × SL) × (±1)',
        },
        {
          stt: '(1.1b)',
          term: 'Giá bán (sau VAT)',
          meaning:
            'Giá bán đã gồm VAT của chính dòng hàng (gross, trước chiết khấu) — bằng giá bán trước VAT cộng phần thuế VAT gốc của tiền hàng. Sản phẩm không chịu thuế / 0% thì bằng giá trước VAT.',
          formula:
            'Giá bán (sau VAT) = Giá bán (trước VAT) + VAT gốc tiền hàng = (Giá trước thuế × (1 + VAT%) × SL) × (±1)',
        },
        {
          stt: '(1.2)',
          term: 'Thuế VAT',
          meaning:
            'Thuế trên dòng sau khi trừ phần thuế nằm trong chiết khấu item và trong phần chiết khấu bill phân bổ cho dòng.',
          formula: 'Thuế VAT = Thuế gốc dòng − thuế trong CK item − thuế trong CK bill (phân bổ)',
        },
        {
          stt: '(1.3)',
          term: 'Phí GH (khách)',
          meaning: 'Phần phí giao hàng thu khách của bill được phân bổ xuống dòng IMEI này.',
          formula: 'Phí GH khách (dòng) = phân bổ(customerShipFee hoặc order) × (±1)',
        },
        {
          stt: '(1.4)',
          term: 'Chiết khấu',
          meaning:
            'Tổng chiết khấu gồm phần tiền + thuế: chiết khấu item + chiết khấu bill (phân bổ) cho dòng.',
          formula: 'Chiết khấu = CK item (pre+tax) + CK bill phân bổ (pre+tax)',
        },
        {
          stt: '(1)',
          term: 'Doanh thu',
          meaning: 'Doanh thu thuần dòng IMEI, cùng cấu trúc như báo cáo theo bill.',
          formula: 'Doanh thu = (1.1) + (1.2) + (1.3) − (1.4)',
        },
        {
          stt: '(2.1)',
          term: 'Giá vốn',
          meaning: 'Giá vốn sản phẩm × SL × (±1).',
          formula: 'Giá vốn = giá vốn đơn vị × SL × (±1)',
        },
        {
          stt: '(2.2)',
          term: 'Thanh toán điểm',
          meaning: 'Phần tiền điểm của bill phân bổ xuống dòng IMEI.',
          formula: 'Thanh toán điểm (dòng) = phân bổ(pointAmount) × (±1)',
        },
        {
          stt: '(2.3)',
          term: 'Phí GH (shop)',
          meaning: 'Phần phí shop trả đối tác VC của bill phân bổ xuống dòng IMEI.',
          formula: 'Phí GH shop (dòng) = phân bổ(shipFee / order) × (±1)',
        },
        {
          stt: '(5)',
          term: 'Lợi nhuận',
          meaning: 'Doanh thu trừ đủ ba khoản chi phí phân bổ.',
          formula: 'Lợi nhuận = Doanh thu − (Giá vốn + Thanh toán điểm + Phí GH shop)',
        },
        {
          stt: '(6)',
          term: 'Lợi nhuận gộp',
          meaning: 'Sau khi trừ VAT và giá vốn; chưa trừ điểm và phí GH shop.',
          formula: 'Lợi nhuận gộp = Doanh thu − Thuế VAT − Giá vốn',
        },
        {
          stt: '(7)',
          term: 'Lợi nhuận ròng',
          meaning: 'Lợi nhuận cuối cùng sau khi trừ thêm thanh toán điểm và phí giao hàng shop.',
          formula: 'Lợi nhuận ròng = Lợi nhuận gộp − Thanh toán điểm − Phí GH shop',
        },
      ],
    }
  },
}
</script>

<style scoped>
.formula-code {
  display: block;
  background: #f4f6fa;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.82rem;
  color: #76767f;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
